import { Bell, ExternalLink, Star } from "lucide-react";
import { useLayoutEffect, useMemo, useRef } from "react";
import {
  formatCurrency,
  formatDate,
  formatNumber,
  formatPercent,
  titleCase
} from "../lib/format";
import { describeTransaction } from "../lib/labels";
import type {
  CanonicalTransaction,
  DashboardIndexes,
  DashboardManifest
} from "../lib/types";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";

export interface TransactionInspectorProps {
  transaction: CanonicalTransaction;
  manifest: DashboardManifest;
  indexes: DashboardIndexes;
  orderedResults: readonly CanonicalTransaction[];
  resultIndex: number;
  favoriteTickers: readonly string[];
  canUseFavorites: boolean;
  favoriteMutationPending: boolean;
  canCreateAlerts?: boolean;
  alertMutationPending?: boolean;
  onToggleFavorite: (ticker: string) => void;
  onCreateTickerAlert?: (ticker: string) => void;
  onSelectResult: (transactionId: string) => void;
  onClose: () => void;
  onOpenIssuer?: (issuerCik: string) => void;
  onOpenOwner?: (ownerIdentityKey: string) => void;
  onOpenCluster?: (clusterId: string) => void;
}


const INSPECTOR_HISTORY_LIMIT = 25;

interface InspectorHistory {
  issuer: readonly CanonicalTransaction[];
  owner: readonly CanonicalTransaction[];
  cluster: readonly CanonicalTransaction[];
  accession: readonly CanonicalTransaction[];
}

function indexedHistory(
  transaction: CanonicalTransaction,
  indexes: DashboardIndexes
): InspectorHistory {
  return {
    issuer: indexes.byIssuer.get(transaction.issuerCik) ?? [],
    owner:
      transaction.ownerIdentityKey === null
        ? []
        : indexes.byOwner.get(transaction.ownerIdentityKey) ?? [],
    cluster:
      transaction.cluster === null
        ? []
        : indexes.byCluster.get(transaction.cluster.clusterId) ?? [],
    accession: indexes.byAccession.get(transaction.accessionNumber) ?? []
  };
}

function tickerPreview(rows: readonly CanonicalTransaction[]): string {
  return rows.length === 0 ? "None" : rows.slice(0, 3).map((row) => row.ticker).join(", ");
}

function yesNo(value: boolean | null | undefined): string {
  return value == null ? "N/A" : value ? "Yes" : "No";
}

function completeList(items: readonly string[] | null | undefined): string {
  return items && items.length > 0 ? items.join(", ") : "None exported.";
}

function exactNumber(value: number | null | undefined): string {
  return value == null ? "N/A" : String(value);
}

function filingLag(value: number | null): string {
  return value === null ? "N/A" : `${exactNumber(value)} ${value === 1 ? "day" : "days"}`;
}

function formatReturnWindow(
  value: { d1: number | null; d5: number | null; d20: number | null } | null | undefined
): string {
  if (!value) return "Not enriched";
  const parts = [value.d1, value.d5, value.d20].map((part) =>
    part == null ? "n/a" : formatPercent(part)
  );
  return parts.join(" / ");
}

export function TransactionInspector(props: TransactionInspectorProps) {
  const {
    transaction,
    manifest,
    indexes,
    orderedResults,
    resultIndex,
    favoriteTickers,
    canUseFavorites,
    favoriteMutationPending,
    canCreateAlerts = false,
    alertMutationPending = false,
    onToggleFavorite,
    onCreateTickerAlert,
    onSelectResult,
    onClose,
    onOpenIssuer,
    onOpenOwner,
    onOpenCluster
  } = props;
  const history = useMemo(
    () => indexedHistory(transaction, indexes),
    [indexes, transaction]
  );
  const isFavorite = favoriteTickers.some(
    (ticker) => ticker.toUpperCase() === transaction.ticker.toUpperCase()
  );
  const isOutsideResults = resultIndex < 0;
  const previous = resultIndex > 0 ? orderedResults[resultIndex - 1] ?? null : null;
  const next =
    resultIndex >= 0 && resultIndex < orderedResults.length - 1
      ? orderedResults[resultIndex + 1] ?? null
      : null;
  const description = describeTransaction(transaction);

  return (
    <div
      data-inspector-focus-root
      tabIndex={-1}
      className="flex h-full min-h-0 flex-col bg-surface"
    >
      <header className="border-b border-border px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="truncate text-sm font-semibold">
              {transaction.ticker} / {transaction.companyName}
            </h2>
            <p className="mt-1 text-xs text-muted-foreground">
              {transaction.insiderName} · {description.activityLabel}
            </p>
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={previous === null}
            onClick={() => previous && onSelectResult(previous.transactionId)}
          >
            Previous result
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={next === null}
            onClick={() => next && onSelectResult(next.transactionId)}
          >
            Next result
          </Button>
          <Button asChild variant="outline" size="sm">
            <a
              href={transaction.secUrl}
              target="_blank"
              rel="noreferrer"
              aria-label="SEC filing"
            >
              <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              SEC filing
            </a>
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-label={`${isFavorite ? "Remove" : "Add"} ${transaction.ticker} ${isFavorite ? "from" : "to"} My watchlist`}
            disabled={!canUseFavorites || favoriteMutationPending}
            onClick={() => onToggleFavorite(transaction.ticker)}
          >
            <Star className="h-3.5 w-3.5" aria-hidden="true" />
            {isFavorite ? "Remove" : "Add"} watchlist
          </Button>
          {canCreateAlerts && onCreateTickerAlert ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-label={`Create filing alert for ${transaction.ticker}`}
              disabled={alertMutationPending}
              onClick={() => onCreateTickerAlert(transaction.ticker)}
            >
              <Bell className="h-3.5 w-3.5" aria-hidden="true" />
              Alert {transaction.ticker}
            </Button>
          ) : null}
        </div>
        {isOutsideResults ? (
          <p className="mt-2 rounded border border-warning bg-warning-soft px-2 py-1.5 text-xs text-warning">
            This transaction is outside the current results.
          </p>
        ) : (
          <p className="mt-2 text-[11px] text-muted-foreground">
            Result {formatNumber(resultIndex + 1)} of {formatNumber(orderedResults.length)}
          </p>
        )}
      </header>

      <Tabs defaultValue="summary" className="flex min-h-0 flex-1 flex-col">
        <div className="border-b border-border px-4 py-2">
          <TabsList aria-label="Inspector sections">
            <TabsTrigger value="summary">Summary</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
            <TabsTrigger value="raw">Raw filing</TabsTrigger>
          </TabsList>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <TabsContent value="summary" className="m-0 space-y-3">
            <InspectorSection title="What happened?">
              <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                <Field label="Value" value={formatCurrency(transaction.value)} />
                <Field label="Shares" value={formatNumber(transaction.shares)} />
                <Field label="Price" value={formatCurrency(transaction.price)} />
                <Field label="Holdings before" value={formatNumber(transaction.holdingsBefore)} />
                <Field label="Holdings after" value={formatNumber(transaction.holdingsAfter)} />
                <Field label="Holdings change" value={formatPercent(transaction.ownershipChangePct)} />
                <Field label="Trade date" value={formatDate(transaction.tradeDate)} />
                <Field label="Filing date" value={formatDate(transaction.filingDate)} />
                <Field label="Filing lag" value={transaction.filingLagDays === null ? "N/A" : `${formatNumber(transaction.filingLagDays)} days`} />
              </dl>
            </InspectorSection>

            <InspectorSection title="Why did this surface?">
              <div className="flex flex-wrap gap-1.5 text-xs">
                <Badge variant={description.badgeVariant}>{description.signalLabel}</Badge>
                <Badge variant="score">
                  Score {transaction.signal.score === null ? "N/A" : transaction.signal.score}
                </Badge>
                <Badge variant="muted">{titleCase(transaction.signal.confidence)} confidence</Badge>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Exported {transaction.signal.version} signal; the browser does not recompute it.
              </p>
              <EvidenceList
                title="Canonical reasons"
                items={transaction.signal.reasons}
                empty="No canonical reasons exported."
              />
              <p className="mt-2 text-xs text-muted-foreground">
                {transaction.cluster
                  ? `Cluster ${transaction.cluster.clusterId}: ${formatNumber(transaction.cluster.distinctInsiderCount)} distinct insiders across ${formatNumber(transaction.cluster.memberTransactionIds.length)} exported transactions.`
                  : "No exported cluster evidence for this transaction."}
              </p>
            </InspectorSection>

            <InspectorSection title="What could make it misleading?">
              <p className="text-xs font-medium">
                {titleCase(transaction.plan.status)} / {transaction.plan.evidenceSource} evidence
              </p>
              <EvidenceList
                title="Plan evidence excerpts"
                items={transaction.plan.evidenceExcerpts}
                empty="No canonical plan evidence excerpts."
              />
              <EvidenceList
                title="Canonical suppressions"
                items={transaction.signal.suppressions}
                empty="No canonical suppressions exported."
              />
              <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                <Field label="Amendment" value={titleCase(transaction.amendmentState)} />
                <Field label="Supersession" value={titleCase(transaction.supersessionState)} />
                <Field label="Value quality" value={titleCase(transaction.valueQuality)} />
                <Field label="Security status" value={titleCase(transaction.securityStatus)} />
              </dl>
              <EvidenceList
                title="Flags"
                items={transaction.flags}
                empty="No exported data-quality flags."
              />
              {(transaction.value === null || transaction.valueQuality === "missing" || transaction.valueQuality === "suspect") ? (
                <p className="mt-2 text-xs text-warning">
                  Transaction value is missing or suspect; compare the normalized fields with the SEC source.
                </p>
              ) : null}
            </InspectorSection>

            <InspectorSection title="What is the surrounding context?">
              <p className="text-xs text-muted-foreground">
                Context is limited to the exported coverage window, not the issuer's complete filing history.
              </p>
              <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                <Field label="Issuer rows" value={formatNumber(history.issuer.length)} />
                <Field label="Owner rows" value={formatNumber(history.owner.length)} />
                <Field label="Cluster rows" value={formatNumber(history.cluster.length)} />
                <Field label="Accession rows" value={formatNumber(history.accession.length)} />
                <Field label="Issuer CIK" value={transaction.issuerCik} />
                <Field label="Owner key" value={transaction.ownerIdentityKey ?? "N/A"} />
                <Field
                  label="Market context"
                  value={
                    transaction.marketContext.source === "missing"
                      ? "Not enriched"
                      : `${transaction.marketContext.marketCapBucket}${
                          transaction.marketContext.valueToMarketCapBps == null
                            ? ""
                            : ` · ${transaction.marketContext.valueToMarketCapBps} bps`
                        }`
                  }
                />
                <Field
                  label="Ownership materiality"
                  value={
                    transaction.marketContext.ownershipMaterialityPct == null
                      ? "N/A"
                      : formatPercent(transaction.marketContext.ownershipMaterialityPct)
                  }
                />
                <Field
                  label="Value / market cap"
                  value={
                    transaction.marketContext.valueToMarketCapBps == null
                      ? "N/A"
                      : `${transaction.marketContext.valueToMarketCapBps} bps`
                  }
                />
                <Field
                  label="Price at trade"
                  value={
                    transaction.marketContext.priceAtTrade == null
                      ? "N/A"
                      : formatCurrency(transaction.marketContext.priceAtTrade)
                  }
                />
                <Field
                  label="Price at filing"
                  value={
                    transaction.marketContext.priceAtFiling == null
                      ? "N/A"
                      : formatCurrency(transaction.marketContext.priceAtFiling)
                  }
                />
                <Field
                  label="Returns from trade (1/5/20d)"
                  value={formatReturnWindow(transaction.marketContext.returnsFromTrade)}
                />
                <Field
                  label="Returns from filing (1/5/20d)"
                  value={formatReturnWindow(transaction.marketContext.returnsFromFiling)}
                />
              </dl>
              <div className="mt-3 flex flex-wrap gap-2">
                {onOpenIssuer ? (
                  <Button type="button" size="sm" variant="outline" onClick={() => onOpenIssuer(transaction.issuerCik)}>
                    Open issuer dossier
                  </Button>
                ) : null}
                {onOpenOwner && transaction.ownerIdentityKey ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => onOpenOwner(transaction.ownerIdentityKey!)}
                  >
                    Open owner dossier
                  </Button>
                ) : null}
                {onOpenCluster && transaction.cluster ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => onOpenCluster(transaction.cluster!.clusterId)}
                  >
                    Open cluster dossier
                  </Button>
                ) : null}
              </div>
              <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                <p>Issuer preview: {tickerPreview(history.issuer)}</p>
                <p>Owner preview: {tickerPreview(history.owner)}</p>
                <p>Cluster preview: {tickerPreview(history.cluster)}</p>
                <p>Accession preview: {tickerPreview(history.accession)}</p>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Filing coverage {manifest.coverage.filingWindowStart} through {manifest.coverage.filingWindowEnd}; trade coverage {manifest.coverage.tradeWindowStart} through {manifest.coverage.tradeWindowEnd}.
              </p>
            </InspectorSection>
          </TabsContent>

          <TabsContent value="history" className="m-0 space-y-3">
            <p className="text-xs text-muted-foreground">
              History is derived only from prebuilt indexes and is limited to the exported coverage window: filings {manifest.coverage.filingWindowStart} through {manifest.coverage.filingWindowEnd}, trades {manifest.coverage.tradeWindowStart} through {manifest.coverage.tradeWindowEnd}.
            </p>
            <HistorySection title="Issuer history" rows={history.issuer} />
            <HistorySection
              title="Owner history"
              rows={history.owner}
              empty={
                transaction.ownerIdentityKey === null
                  ? "No owner identity key was exported; no name-based scan is performed."
                  : undefined
              }
            />
            <HistorySection title="Cluster history" rows={history.cluster} />
            <HistorySection title="Accession history" rows={history.accession} />
          </TabsContent>

          <TabsContent value="raw" className="m-0 space-y-3">
            <p className="text-xs text-muted-foreground">
              This is normalized evidence, not the entire filing. Use the exact SEC link for the complete source.
            </p>
            <section
              aria-label="Normalized filing fields"
              className="rounded border border-border p-3"
            >
              <h3 className="text-sm font-semibold">Normalized filing fields</h3>
              <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                <Field label="Record ID" value={transaction.id} />
                <Field label="Transaction ID" value={transaction.transactionId} />
                <Field label="Ticker" value={transaction.ticker} />
                <Field label="Company" value={transaction.companyName} />
                <Field label="Exchange" value={transaction.exchange} />
                <Field label="Industry" value={transaction.industryDescription} />
                <Field label="Sector" value={transaction.sector} />
                <Field label="Accession" value={transaction.accessionNumber} />
                <Field label="Issuer CIK" value={transaction.issuerCik} />
                <Field label="CIK" value={transaction.cik} />
                <Field label="Owner CIK" value={transaction.ownerCik ?? "N/A"} />
                <Field label="Owner identity key" value={transaction.ownerIdentityKey ?? "N/A"} />
                <Field label="Insider" value={transaction.insiderName} />
                <Field label="Relationship flags" value={completeList(transaction.relationshipFlags)} />
                <Field label="Primary role" value={transaction.role} />
                <Field label="Relationship" value={transaction.relationship} />
                <Field label="Activity" value={titleCase(transaction.tradeType)} />
                <Field label="Transaction code" value={transaction.transactionCode} />
                <Field
                  label="Direction"
                  value={`${transaction.acquiredDisposed} (${transaction.acquiredDisposed === "A" ? "Acquired" : "Disposed"})`}
                />
                <Field label="Shares" value={exactNumber(transaction.shares)} />
                <Field label="Price" value={exactNumber(transaction.price)} />
                <Field label="Value" value={exactNumber(transaction.value)} />
                <Field label="Value quality" value={titleCase(transaction.valueQuality)} />
                <Field label="Holdings before" value={exactNumber(transaction.holdingsBefore)} />
                <Field label="Holdings after" value={exactNumber(transaction.holdingsAfter)} />
                <Field label="Holdings change" value={exactNumber(transaction.ownershipChangePct)} />
                <Field label="Trade date" value={formatDate(transaction.tradeDate)} />
                <Field label="Filing date" value={formatDate(transaction.filingDate)} />
                <Field label="Filing lag" value={filingLag(transaction.filingLagDays)} />
                <Field label="Security title" value={transaction.securityTitle} />
                <Field label="Security status" value={titleCase(transaction.securityStatus)} />
                <Field label="Common equity" value={yesNo(transaction.isCommonEquity)} />
                <Field label="Direct ownership" value={yesNo(transaction.directOwnership)} />
                <Field label="Derivative" value={yesNo(transaction.isDerivative)} />
                <Field label="Plan status" value={titleCase(transaction.plan.status)} />
                <Field label="Plan evidence source" value={titleCase(transaction.plan.evidenceSource)} />
                <Field label="Planned" value={yesNo(transaction.isPlanned)} />
                <Field label="Amendment filing" value={yesNo(transaction.isAmendment)} />
                <Field label="Amendment state" value={titleCase(transaction.amendmentState)} />
                <Field label="Supersession state" value={titleCase(transaction.supersessionState)} />
                <Field label="Flags" value={completeList(transaction.flags)} />
                <Field label="Cluster ID" value={transaction.cluster?.clusterId ?? "N/A"} />
                <Field label="Cluster version" value={transaction.cluster?.version ?? "N/A"} />
                <Field label="Cluster window start" value={formatDate(transaction.cluster?.windowStart)} />
                <Field label="Cluster window end" value={formatDate(transaction.cluster?.windowEnd)} />
                <Field
                  label="Cluster member transaction IDs"
                  value={completeList(transaction.cluster?.memberTransactionIds)}
                />
                <Field
                  label="Cluster distinct insiders"
                  value={exactNumber(transaction.cluster?.distinctInsiderCount)}
                />
                <Field
                  label="Program same ticker/date/price cluster"
                  value={yesNo(transaction.programContext?.sameTickerDatePriceCluster)}
                />
                <Field
                  label="Program group size"
                  value={exactNumber(transaction.programContext?.groupSize)}
                />
                <Field
                  label="Program distinct owners"
                  value={exactNumber(transaction.programContext?.distinctOwnerCount)}
                />
                <Field label="Signal bucket" value={titleCase(transaction.signal.bucket)} />
                <Field label="Signal score" value={exactNumber(transaction.signal.score)} />
                <Field label="Signal confidence" value={titleCase(transaction.signal.confidence)} />
                <Field label="Signal version" value={transaction.signal.version} />
                <Field label="Source URL" value={transaction.secUrl} />
              </dl>
              <Button asChild variant="outline" size="sm" className="mt-3">
                <a href={transaction.secUrl} target="_blank" rel="noreferrer" aria-label="SEC filing">
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                  SEC filing
                </a>
              </Button>
            </section>
            <RawEvidence title="Complete source footnotes" items={transaction.footnotes} />
            <RawEvidence title="Complete plan evidence" items={transaction.plan.evidenceExcerpts} />
            <RawEvidence title="Canonical reasons" items={transaction.signal.reasons} />
            <RawEvidence title="Canonical suppressions" items={transaction.signal.suppressions} />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}

export function TransactionInspectorSheet(props: TransactionInspectorProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;

    if (!dialog.open) {
      if (typeof dialog.showModal === "function") dialog.showModal();
      else dialog.setAttribute("open", "");
    }
    dialog
      .querySelector<HTMLElement>("[data-inspector-focus-root]")
      ?.focus({ preventScroll: true });

    return () => {
      if (!dialog.open) return;
      if (typeof dialog.close === "function") dialog.close();
      else dialog.removeAttribute("open");
    };
  }, []);

  return (
    <dialog
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Transaction inspector"
      aria-describedby="transaction-inspector-description"
      className="transaction-inspector-dialog animate-sheet-in motion-reduce:animate-none"
      onCancel={(event) => {
        event.preventDefault();
        props.onClose();
      }}
    >
      <p id="transaction-inspector-description" className="sr-only">
        Research context and normalized evidence for the selected insider transaction.
      </p>
      <TransactionInspector {...props} />
    </dialog>
  );
}

function InspectorSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded border border-border bg-muted/40 p-3">
      <h3 className="text-sm font-semibold">{title}</h3>
      <div className="mt-2">{children}</div>
    </section>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="break-words font-medium text-foreground">{value}</dd>
    </div>
  );
}

function EvidenceList({
  title,
  items,
  empty
}: {
  title: string;
  items: readonly string[];
  empty: string;
}) {
  return (
    <div className="mt-3">
      <p className="text-[11px] font-semibold uppercase text-muted-foreground">{title}</p>
      <ul className="mt-1 list-disc space-y-1 pl-4 text-xs text-muted-foreground">
        {items.length > 0
          ? items.map((item, index) => <li key={`${index}:${item}`}>{item}</li>)
          : <li>{empty}</li>}
      </ul>
    </div>
  );
}

function HistorySection({
  title,
  rows,
  empty = "No related transactions in the exported coverage window."
}: {
  title: string;
  rows: readonly CanonicalTransaction[];
  empty?: string;
}) {
  const previewRows = rows.slice(0, INSPECTOR_HISTORY_LIMIT);
  return (
    <section role="region" aria-label={title} className="rounded border border-border p-3">
      <h3 className="text-sm font-semibold">
        {title} <span className="text-muted-foreground">({formatNumber(rows.length)})</span>
      </h3>
      {rows.length > 0 ? (
        <>
          {rows.length > previewRows.length ? (
            <p className="mt-2 text-xs text-muted-foreground">
              Showing first {formatNumber(previewRows.length)} of {formatNumber(rows.length)}
            </p>
          ) : null}
          <ul className="mt-2 divide-y divide-border text-xs">
            {previewRows.map((row) => (
              <li
                key={row.transactionId}
                data-history-transaction-id={row.transactionId}
                className="flex items-start justify-between gap-3 py-2"
              >
                <span>
                  <strong>{row.ticker}</strong> · {row.insiderName}
                </span>
                <span className="shrink-0 text-muted-foreground">
                  {formatDate(row.tradeDate)} / {formatDate(row.filingDate)}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="mt-2 text-xs text-muted-foreground">{empty}</p>
      )}
    </section>
  );
}

function RawEvidence({ title, items }: { title: string; items: readonly string[] }) {
  return (
    <section className="rounded border border-border p-3">
      <h3 className="text-sm font-semibold">{title}</h3>
      <ol className="mt-2 list-decimal space-y-2 pl-4 text-xs text-muted-foreground">
        {items.length > 0
          ? items.map((item, index) => (
              <li key={`${index}:${item}`} className="whitespace-pre-wrap break-words">
                {item}
              </li>
            ))
          : <li>None exported.</li>}
      </ol>
    </section>
  );
}
